import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Settings, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const KIND_TABS = [
  { id: "all",         label: "All",      match: null },
  { id: "friend",      label: "Friends",  match: (n) => n.kind === "friend-beat" || n.kind === "friend-challenge" },
  { id: "achievement", label: "Trophies", match: (n) => n.kind === "achievement" },
  { id: "tournament",  label: "Brackets", match: (n) => n.kind === "tournament-ai-filled" || n.kind === "tournament-forfeit" },
  { id: "shield",      label: "Shields",  match: (n) => n.kind === "shield-purchased" || n.kind === "streak-reminder" },
  { id: "system",      label: "System",   match: (n) => n.kind === "daily-seed" || n.kind === "weekly-featured" || n.kind === "first-blood" },
];

// User-facing labels for each muteable notification kind.
const KIND_LABELS = {
  "friend-beat":         "Friends beating your scores",
  "friend-challenge":    "Challenge/nudge invites",
  "achievement":         "Trophy unlocks",
  "tournament-ai-filled": "AI-filled bracket ready alerts",
  "shield-purchased":    "Streak Shield purchases",
  "streak-reminder":     "Pre-midnight streak warnings",
  "daily-seed":          "Daily seed alerts",
  "weekly-featured":     "Weekly featured rotation",
  "first-blood":         "First-blood boss kills (global)",
};

/**
 * NotificationBell — navbar dropdown for in-app notifications.
 *
 * Polls GET /api/notifications every 45s to surface friend-beat + achievement
 * unlock events. Shows a pink unread-count pill over the bell icon when count>0.
 * Clicking an item navigates to its url; clicking "Mark all read" clears the
 * unread badge server-side.
 */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [muted, setMuted] = useState([]);
  const [availableKinds, setAvailableKinds] = useState([]);
  const ref = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch (_) { /* unauthed — noop */ }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 45000);
    return () => clearInterval(id);
  }, [refresh]);

  // Fetch notification preferences once when the dropdown is opened (lazy).
  useEffect(() => {
    if (!open) return;
    api.get("/notifications/preferences")
      .then((r) => {
        setMuted(r.data.muted_kinds || []);
        setAvailableKinds(r.data.available_kinds || []);
      })
      .catch(() => { /* noop — unauth */ });
  }, [open]);

  const toggleKindMute = async (kind) => {
    const next = muted.includes(kind) ? muted.filter((k) => k !== kind) : [...muted, kind];
    setMuted(next);
    try {
      await api.patch("/notifications/preferences", { muted_kinds: next });
    } catch (_) { /* noop */ }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((list) => list.map((n) => ({ ...n, read: true })));
    } catch (_) { /* noop */ }
  };

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (_) { /* noop */ }
  };

  // Delete a single notification — optimistic so the row disappears
  // instantly. The corresponding unread count is decremented only if
  // the deleted row was actually unread. Stops link propagation in
  // the row click handler so deleting doesn't also navigate.
  const deleteOne = async (id) => {
    const target = items.find((n) => n.id === id);
    const wasUnread = !!target && !target.read;
    setItems((list) => list.filter((n) => n.id !== id));
    if (wasUnread) setUnread((u) => Math.max(0, u - 1));
    try {
      await api.delete(`/notifications/${id}`);
    } catch (err) {
      // Roll back optimism on failure — refetch from source of truth.
      toast.error("Couldn't delete notification");
      refresh();
    }
  };

  // Clear every notification for the current pilot. Surfaces a toast
  // with the count cleared so it doesn't feel like a silent no-op.
  const deleteAll = async () => {
    if (items.length === 0) return;
    const prev = items;
    const prevUnread = unread;
    setItems([]);
    setUnread(0);
    try {
      const r = await api.delete("/notifications");
      toast.success(`Cleared ${r.data?.deleted ?? prev.length} notification${(r.data?.deleted ?? prev.length) === 1 ? "" : "s"}`);
    } catch (err) {
      setItems(prev);
      setUnread(prevUnread);
      toast.error("Couldn't clear notifications");
    }
  };

  const filtered = useMemo(() => {
    const t = KIND_TABS.find((k) => k.id === tab);
    if (!t || !t.match) return items;
    return items.filter(t.match);
  }, [items, tab]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const t of KIND_TABS) {
      if (t.match) c[t.id] = items.filter(t.match).length;
    }
    return c;
  }, [items]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 border border-white/15 hover:border-[#00ffff] text-white/80 hover:text-[#00ffff] flex items-center justify-center transition"
        title="Notifications"
        data-testid="nav-notification-btn"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff007f] text-white font-mono-cosmic text-[9px] font-bold flex items-center justify-center"
            style={{ boxShadow: "0 0 8px rgba(255,0,127,0.7)" }}
            data-testid="nav-notification-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-11 w-[calc(100vw-1rem)] sm:w-80 max-h-[70vh] sm:max-h-[460px] border border-white/15 overflow-hidden flex flex-col z-[9999]"
          style={{
            // Solid background so the dropdown is readable even on browsers
            // that ignore backdrop-filter (Android Chrome / Samsung Internet).
            background: "#08080e",
            boxShadow: "0 8px 24px rgba(0,0,0,0.7)",
          }}
          data-testid="nav-notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div>
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#00ffff] uppercase">
                // NOTIFICATIONS
              </div>
              <div className="font-heading uppercase text-sm tracking-tight text-white">
                {unread > 0 ? `${unread} unread` : "All caught up"}
              </div>
            </div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/60 hover:text-[#00ff41] inline-flex items-center gap-1 transition"
                data-testid="nav-notification-mark-all"
              >
                <Check className="w-3 h-3" /> Mark all
              </button>
            )}
            {items.length > 0 && (
              <button
                onClick={deleteAll}
                className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/60 hover:text-[#ff007f] inline-flex items-center gap-1 transition"
                title="Delete every notification"
                data-testid="nav-notification-clear-all"
              >
                <Trash2 className="w-3 h-3" /> Clear all
              </button>
            )}
            <button
              onClick={() => setPrefsOpen((p) => !p)}
              className={`w-7 h-7 border transition flex items-center justify-center ${
                prefsOpen ? "border-[#00ffff] text-[#00ffff]" : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
              }`}
              title="Preferences"
              data-testid="nav-notification-prefs-btn"
            >
              <Settings className="w-3 h-3" />
            </button>
          </div>

          {prefsOpen && (
            <div
              className="px-4 py-3 border-b border-white/10 bg-black/40"
              data-testid="nav-notification-prefs-panel"
            >
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#00ffff] mb-2">
                // MUTE KINDS
              </div>
              {availableKinds.length === 0 ? (
                <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/40">
                  // LOADING…
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {availableKinds.map((kind) => {
                    const isMuted = muted.includes(kind);
                    return (
                      <li key={kind} className="flex items-center justify-between gap-2">
                        <span className="text-xs text-white/70 truncate">
                          {KIND_LABELS[kind] || kind}
                        </span>
                        <button
                          onClick={() => toggleKindMute(kind)}
                          className={`px-2 py-1 border font-mono-cosmic text-[9px] tracking-[0.25em] uppercase transition ${
                            isMuted
                              ? "border-[#ff007f] text-[#ff007f] bg-[#ff007f]/10"
                              : "border-white/15 text-white/60 hover:text-white"
                          }`}
                          data-testid={`nav-notification-mute-${kind}`}
                        >
                          {isMuted ? "Muted" : "On"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          <div className="flex items-center gap-1 px-3 py-2 border-b border-white/10 overflow-x-auto" data-testid="nav-notification-tabs">
            {KIND_TABS.map((t) => {
              const count = counts[t.id] || 0;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-2 py-1 font-mono-cosmic text-[9px] tracking-[0.25em] uppercase transition whitespace-nowrap border ${
                    active
                      ? "border-[#00ffff] text-[#00ffff] bg-[#00ffff]/10"
                      : "border-transparent text-white/60 hover:text-white"
                  }`}
                  data-testid={`nav-notification-tab-${t.id}`}
                >
                  {t.label} {count > 0 && <span className="text-white/40">· {count}</span>}
                </button>
              );
            })}
          </div>

          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-8 text-center font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/40">
                // {tab === "all" ? "NO ALERTS YET" : "NOTHING IN THIS BUCKET"}
              </div>
            ) : (
              <ul>
                {filtered.map((n) => (
                  <li
                    key={n.id}
                    className={`relative border-b border-white/5 last:border-0 group ${n.read ? "opacity-60" : ""}`}
                    data-testid={`notif-${n.id}`}
                  >
                    <Link
                      to={n.url || "/"}
                      onClick={() => { markOne(n.id); setOpen(false); }}
                      className="block px-4 py-3 pr-9 hover:bg-white/5 transition"
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className="w-1.5 h-1.5 mt-1.5 rounded-full flex-shrink-0"
                          style={{
                            background:
                              n.kind === "friend-beat" ? "#ff007f" :
                              n.kind === "friend-challenge" ? "#ffd700" :
                              n.kind === "achievement" ? "#ffd700" :
                              n.kind === "tournament-ai-filled" ? "#b026ff" :
                              n.kind === "tournament-forfeit" ? "#ff8c00" :
                              n.kind === "daily-seed" ? "#00ffff" :
                              n.kind === "weekly-featured" ? "#b026ff" :
                              "#8b949e",
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-heading uppercase text-xs tracking-tight text-white truncate">
                            {n.title}
                          </div>
                          <div className="text-white/60 text-xs mt-0.5 line-clamp-2">
                            {n.body}
                          </div>
                          <div className="font-mono-cosmic text-[8px] tracking-[0.25em] uppercase text-white/30 mt-1">
                            {n.kind.replace(/-/g, " ")}
                          </div>
                        </div>
                      </div>
                    </Link>
                    {/* Per-row delete X — absolute-positioned so it
                        floats over the right edge of the row without
                        interfering with the navigation Link. Only
                        visible on hover/focus so the row stays clean
                        when idle. preventDefault + stopPropagation
                        kill the Link click so we don't navigate
                        before deleting. */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        deleteOne(n.id);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-white/30 hover:text-[#ff007f] hover:bg-[#ff007f]/10 border border-transparent hover:border-[#ff007f]/40 transition opacity-0 group-hover:opacity-100 focus:opacity-100"
                      title="Delete notification"
                      data-testid={`notif-delete-${n.id}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
